import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Users, Plus, Trash2, Edit3 } from "lucide-react";

export default function Teams() {
  const { user, loading } = useAuth();
  const [teams, setTeams] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ team_naam: "", vereniging: "", niveau: "A", leeftijd: "jeugd", toestel: "mt", aantal_gezamenlijk: 6 });
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const load = () => api.get("/teams").then(r => setTeams(r.data)).catch(e => setErr(formatError(e)));
  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="text-slate-500">Laden...</div>;
  if (!user) return <Navigate to="/login" replace/>;

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const r = await api.post("/teams", { ...form, onderdeel: "groepspringen", gezamenlijke_sprongen: [], turners: [] });
      nav(`/teams/${r.data.id}`);
    } catch (e) { setErr(formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Team verwijderen?")) return;
    await api.delete(`/teams/${id}`); load();
  };

  return (
    <div data-testid="teams-page">
      <div className="text-xs uppercase tracking-[0.22em] text-orange-600 font-semibold">Trainer</div>
      <h1 className="heading-display text-4xl sm:text-5xl mt-1 mb-3">Team beheer</h1>
      <p className="text-slate-600 mb-6">Beheer teams voor groepspringen: 4 of 6 gezamenlijke sprongen + 2 keuzesprongen per turner.</p>

      {creating ? (
        <form onSubmit={create} className="gs-card p-5 mb-6 space-y-3" data-testid="team-create-form">
          <h2 className="font-bold">Nieuw team</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <input required placeholder="Team naam" value={form.team_naam} onChange={e => setForm({...form, team_naam: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="team-naam"/>
            <input placeholder="Vereniging (optioneel)" value={form.vereniging} onChange={e => setForm({...form, vereniging: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="team-vereniging"/>
            <select value={form.leeftijd} onChange={e => setForm({...form, leeftijd: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="team-leeftijd">
              <option value="jeugd">Jeugd (8-12)</option>
              <option value="junior">Junior (13-17)</option>
              <option value="senior">Senior (18+)</option>
            </select>
            <select value={form.niveau} onChange={e => setForm({...form, niveau: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="team-niveau">
              <option>C</option><option>B</option><option>A</option><option>A-ere</option>
            </select>
            <select value={form.toestel} onChange={e => setForm({...form, toestel: e.target.value})} className="px-3 py-2 border rounded-lg" data-testid="team-toestel">
              <option value="mt">Mini-trampoline</option>
              <option value="pegasus">Pegasus</option>
              <option value="airtrack">Airtrack</option>
            </select>
            <select value={form.aantal_gezamenlijk} onChange={e => setForm({...form, aantal_gezamenlijk: parseInt(e.target.value)})} className="px-3 py-2 border rounded-lg" data-testid="team-aantal">
              <option value={4}>4 gezamenlijke sprongen</option>
              <option value={6}>6 gezamenlijke sprongen</option>
            </select>
          </div>
          {err && <div className="text-red-600 text-sm">{err}</div>}
          <div className="flex gap-2">
            <button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg font-semibold" data-testid="team-create-submit">Aanmaken</button>
            <button type="button" onClick={() => setCreating(false)} className="text-slate-600 px-4 py-2">Annuleren</button>
          </div>
        </form>
      ) : (
        <button onClick={() => setCreating(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold flex items-center gap-2 mb-6" data-testid="new-team-btn">
          <Plus size={16}/> Nieuw team
        </button>
      )}

      {teams.length === 0 ? (
        <div className="gs-card p-6 text-slate-600 text-center" data-testid="teams-empty">Nog geen teams. Maak je eerste team aan hierboven.</div>
      ) : (
        <ul className="space-y-3" data-testid="teams-list">
          {teams.map(t => (
            <li key={t.id} className="gs-card p-4 flex items-center justify-between" data-testid={`team-${t.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><Users className="text-orange-600" size={20}/></div>
                <div>
                  <div className="font-bold">{t.team_naam}</div>
                  <div className="text-sm text-slate-600">{t.vereniging} · {t.leeftijd} · niveau {t.niveau} · {t.toestel} · {t.aantal_gezamenlijk} gez. + {t.turners?.length || 0} turners</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/teams/${t.id}`} className="text-orange-600 hover:text-orange-700 p-2 inline-flex items-center gap-1 text-sm font-semibold" data-testid={`edit-${t.id}`}><Edit3 size={16}/> Beheren</Link>
                <button onClick={() => del(t.id)} className="text-slate-400 hover:text-red-600 p-2" data-testid={`del-${t.id}`}><Trash2 size={16}/></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
